import crypto from 'crypto';

/**
 * Akamai EdgeGrid timestamp in `yyyyMMddTHH:mm:ss+0000` format (UTC).
 * Matches httpie-edgegrid and akamai-edgegrid-python.
 */
export function edgeGridTimestamp(date: Date = new Date()): string {
  const pad = (value: number): string => String(value).padStart(2, '0');

  return (
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}+0000`
  );
}

function base64HmacSha256(data: string, key: string): string {
  return crypto.createHmac('sha256', key).update(data).digest('base64');
}

function sha256Hash(data: string): string {
  return crypto.createHash('sha256').update(data).digest('base64');
}

export interface SignEdgeGridRequestOptions {
  method: string;
  url: string;
  body: string;
  clientToken: string;
  accessToken: string;
  clientSecret: string;
  timestamp?: string;
  nonce?: string;
}

/**
 * Signs a request using Akamai EdgeGrid v1.
 * Returns the full Authorization header value.
 */
export function signEdgeGridRequest(opts: SignEdgeGridRequestOptions): string {
  const { method, url, body, clientToken, accessToken, clientSecret } = opts;
  const timestamp = opts.timestamp ?? edgeGridTimestamp();
  const nonce = opts.nonce ?? crypto.randomUUID();

  const authDataPrefix =
    `EG1-HMAC-SHA256 client_token=${clientToken};` +
    `access_token=${accessToken};` +
    `timestamp=${timestamp};` +
    `nonce=${nonce};`;

  const parsedUrl = new URL(url);
  const requestPath = parsedUrl.pathname + (parsedUrl.search ? parsedUrl.search : '');
  const bodyHash = method.toUpperCase() === 'POST' && body ? sha256Hash(body) : '';

  const dataToSign = [
    method.toUpperCase(),
    'https',
    parsedUrl.host,
    requestPath,
    '',
    bodyHash,
    authDataPrefix,
  ].join('\t');

  const signingKey = base64HmacSha256(timestamp, clientSecret);
  const signature = base64HmacSha256(dataToSign, signingKey);

  return `${authDataPrefix}signature=${signature}`;
}
